import { useState, useRef } from 'react';
import { fileAPI } from '../lib/api';
import { Button } from './ui/button';
import { Camera, CircleNotch, Trash, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * MaterialImageUpload - Upload & preview image for a material
 * @param {string} imageUrl - Current image URL
 * @param {function} onImageChange - Callback with new URL or null
 * @param {boolean} readOnly - Disable upload, show only
 * @param {string} size - 'sm' | 'md' for thumbnail size
 */
const MaterialImageUpload = ({ imageUrl, onImageChange, readOnly = false, size = 'sm' }) => {
  const [uploading, setUploading] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Solo JPG, PNG o WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Máximo 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fileAPI.uploadMaterialImage(formData);
      onImageChange?.(res.data.url);
      toast.success('Imagen subida');
    } catch {
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onImageChange?.(null);
  };

  const dims = size === 'md' ? 'w-20 h-20' : 'w-14 h-14';

  if (imageUrl) {
    return (
      <>
        <div className="relative group inline-block" data-testid="material-image-preview">
          <img
            src={imageUrl}
            alt="Material"
            className={`${dims} object-cover border border-zinc-200 dark:border-zinc-700 cursor-pointer transition-transform hover:scale-105`}
            onClick={() => setShowLightbox(true)}
          />
          {!readOnly && (
            <button
              onClick={handleRemove}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid="material-image-remove"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Lightbox */}
        {showLightbox && (
          <div
            className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
            data-testid="material-image-lightbox"
          >
            <div className="relative max-w-3xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
              <img
                src={imageUrl}
                alt="Material"
                className="max-w-full max-h-[85vh] object-contain border-2 border-white shadow-2xl"
              />
              <button
                onClick={() => setShowLightbox(false)}
                className="absolute -top-3 -right-3 w-8 h-8 bg-white text-zinc-900 rounded-full flex items-center justify-center shadow-lg hover:bg-zinc-100 transition-colors"
                data-testid="lightbox-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (readOnly) return null;

  return (
    <div data-testid="material-image-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleUpload}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={`${dims} border-2 border-dashed border-zinc-300 dark:border-zinc-600 flex items-center justify-center text-zinc-400 hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer`}
        title="Subir imagen del material (opcional)"
        data-testid="material-image-upload-btn"
      >
        {uploading ? (
          <CircleNotch className="w-5 h-5 animate-spin" />
        ) : (
          <Camera className="w-5 h-5" />
        )}
      </button>
    </div>
  );
};

export default MaterialImageUpload;
